/* eslint-disable react-hooks/exhaustive-deps */
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { prepareHeaders } from "../user/user.api";
import { PaginationResults, TypeQuery } from "../user/user.type";
import { ApiBaseUrl } from "../../http";
import { QueryUrl, useLocationState } from '../../Utils';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { IPrestataire, PrestataireFormdata, INote , NoteFormdata } from './prestataire.type';
import { ICity } from '../logement/logement.type';

const PrestataireApi = createApi({
    reducerPath: "prestataireApi",
    tagTypes: ["prestataire","historiquesByPrestataire","notesByPrestataire", "ville"],
    baseQuery: fetchBaseQuery({
        baseUrl: `${ApiBaseUrl}/api/`,
        prepareHeaders,
    }),
    endpoints: (build) => ({
        AddOrUpdatePrestataire: build.mutation<
            IPrestataire,
            { slug?: string; data: FormData | PrestataireFormdata }
        >({
            query: ({ slug, data }) => ({
                url: slug ? `users/${slug}/` : `users/`,
                method: slug ? "PUT" : "POST",
                body: data,
            }),
            invalidatesTags: ["prestataire","notesByPrestataire"],
        }),
        AddOrUpdateNote: build.mutation<
            INote,
            { slug?: string; data: INote | FormData | Partial<INote> }
        >({
            query: ({ slug, data }) => ({
                url: slug ? `notes/${slug}/` : `notes/`,
                method: slug ? "PUT" : "POST",
                body: data,
            }),
            invalidatesTags: ["prestataire","notesByPrestataire"],
        }),
        getPrestataires: build.query<PaginationResults<IPrestataire>, TypeQuery>({
            query: (query) => QueryUrl("prestataires", query),
            providesTags: ["prestataire"],
        }),
        getPrestataire: build.query<IPrestataire, string>({
            query: (slug) => `prestataires/${slug}`,
            providesTags: ["prestataire"],
        }),
        deletePrestataire: build.mutation<PrestataireFormdata, string>({
            query: (slug) => ({
                url: `users/${slug}/`,
                method: "DELETE",
            }),
            invalidatesTags: ["prestataire"],
        }),
        getMissionByPrestataire: build.query<PaginationResults<IPrestataire>, TypeQuery>({
            query: ({ slug, ...query }) => QueryUrl(`prestataire/${slug}/missions/`, query),
            providesTags: ["prestataire"],
        }),
        getListMissionsByPrestataire: build.query<IPrestataire[], TypeQuery>({
            query: ({ slug, ...query }) => QueryUrl(`list/prestataire/${slug}/missions/`, query),
            providesTags: ["prestataire"],
        }),
        getNotesByPrestataire: build.query<PaginationResults<INote>, TypeQuery>({
            query: ({ slug, ...query }) => QueryUrl(`prestataire/${slug}/notes/`, query),
            providesTags: ['notesByPrestataire'],
        }),
        getHistoriquesByPrestataire: build.query<PaginationResults<IPrestataire>, TypeQuery>({
            query: ({ slug, ...query }) => QueryUrl(`prestataire/${slug}/historiques/`, query),
            providesTags: ['historiquesByPrestataire'],
        }),
        getPrestataireByDistanceLogement: build.query<PaginationResults<IPrestataire>, TypeQuery>({
            query: ({ slug, ...query }) => QueryUrl(`distance/logement/${slug}/prestataires/`, query),
        }),

        deleteNote: build.mutation<NoteFormdata, string>({
            query: (slug) => ({
                url: `notes/${slug}/`,
                method: "DELETE",
            }),
            invalidatesTags: ["prestataire",'notesByPrestataire'],
        }),
        getPrestataireVilles: build.query<ICity[], TypeQuery>({
            query: (query) => QueryUrl("prestataire/villes", query),
            providesTags: ["ville"],
        }),
    }),
});

export default PrestataireApi

export const {
    useAddOrUpdatePrestataireMutation,
    useAddOrUpdateNoteMutation,
    useGetPrestatairesQuery,
    useDeletePrestataireMutation,
    useLazyGetPrestataireQuery,
    useGetMissionByPrestataireQuery,
    useGetListMissionsByPrestataireQuery,
    useGetNotesByPrestataireQuery,
    useGetHistoriquesByPrestataireQuery,
    useLazyGetPrestataireByDistanceLogementQuery,
    useDeleteNoteMutation,
    useLazyGetListMissionsByPrestataireQuery,
    useGetPrestataireVillesQuery

} = PrestataireApi;

export function usePrestataireFromLocation(): [
    IPrestataire,
    boolean,
    string,
    (id: string) => any
] {
    const itemState = useLocationState<IPrestataire | undefined>(undefined);
    const [item, setItem] = useState(itemState);
    const { slug } = useParams<{ slug: string }>();
    const [findById, { data, isError, isLoading }] = useLazyGetPrestataireQuery();
    const navigate = useNavigate();

    useEffect(() => {
        if (slug) {
            findById(slug);
        }
    }, [slug]);
    useEffect(() => {
        if (isError && !itemState) {
            navigate(-1);
        }
    }, [isError]);
    useEffect(() => {
        if (data) {
            console.log("data", data);
            setItem(data);
        }
    }, [data]);

    return [item as IPrestataire, isLoading, slug as string, findById];
}